/**
 * The intake plugin keeps its own form in view: on every `focus-input` it measures how
 * far the form's bottom edge sits past the bottom of the viewport and closes the gap
 * with `window.scrollBy` (its only call to it — `dist/index.js:4966` in 7.2.0).
 * The first `focus-input` fires as soon as the opening module sets up, before the
 * visitor has done anything.
 *
 * That is the right instinct on every other page, where the form is the hero and the
 * gap is zero. On the directory the form sits below the listings, so it drags the
 * visitor away from the attorneys they came to read.
 *
 * The scroll only earns its keep once the visitor is actually using the form, so that
 * is what we gate on. Asking instead whether the form is on screen looks equivalent and
 * is not: on a tall viewport the top of the form clears the fold while the visitor is
 * still at the top of the page, and the Colorado tab — shorter than California's — pulls
 * it a couple of hundred pixels higher still.
 *
 * Patching `window.scrollBy` is the narrowest hold we have. The plugin exposes no way to
 * turn this off, and it is the only caller of `scrollBy` in the bundle, so on this page
 * the patch intercepts nothing else.
 *
 * @returns {Function} Restores the native `window.scrollBy`.
 */
export default function preventIntakeSelfScroll() {
    const nativeScrollBy = window.scrollBy;
    const engagementEvents = ['focusin', 'pointerdown', 'keydown', 'touchstart'];

    let visitorHasEngaged = false;

    const stopWatchingForEngagement = () => {
        engagementEvents.forEach((name) => document.removeEventListener(name, noteEngagement, true));
    };

    /**
     * Clicking Connect on a listing counts: it focuses the form's first input, which is
     * the visitor asking to be taken there.
     */
    function noteEngagement(event) {
        if (event.target.closest?.('#directory-intake')) {
            visitorHasEngaged = true;
            stopWatchingForEngagement();
        }
    }

    engagementEvents.forEach((name) => document.addEventListener(name, noteEngagement, true));

    window.scrollBy = function (...args) {
        if (visitorHasEngaged) {
            return nativeScrollBy.apply(this, args);
        }

        return undefined;
    };

    return () => {
        stopWatchingForEngagement();
        window.scrollBy = nativeScrollBy;
    };
}
